/**
 * Utility per la gestione degli eventi di aggiornamento crediti
 * Centralizza la logica per notificare i cambiamenti dei crediti in tutta l'applicazione
 */

export const CREDITS_EVENTS = {
  CREDITS_UPDATED: 'creditsUpdated',
  CREDITS_SPENT: 'creditsSpent',
  CREDITS_ADDED: 'creditsAdded',
  CREDITS_REFUNDED: 'creditsRefunded'
} as const;

/**
 * Emette un evento personalizzato per notificare l'aggiornamento dei crediti
 * @param eventType - Tipo di evento da emettere
 * @param data - Dati aggiuntivi da includere nell'evento
 */
export const emitCreditsEvent = (eventType: string, data?: any) => {
  const event = new CustomEvent(eventType, { detail: data });
  window.dispatchEvent(event);
};

/**
 * Emette l'evento standard di aggiornamento crediti
 */
export const notifyCreditsUpdated = () => {
  emitCreditsEvent(CREDITS_EVENTS.CREDITS_UPDATED);
};

/**
 * Emette un evento quando i crediti vengono spesi
 * @param amount - Quantità di crediti spesi
 */
export const notifyCreditsSpent = (amount: number) => {
  emitCreditsEvent(CREDITS_EVENTS.CREDITS_SPENT, { amount });
};

/**
 * Emette un evento quando i crediti vengono aggiunti
 * @param amount - Quantità di crediti aggiunti
 */
export const notifyCreditsAdded = (amount: number) => {
  emitCreditsEvent(CREDITS_EVENTS.CREDITS_ADDED, { amount });
};

/**
 * Emette un evento quando i crediti vengono rimborsati
 * @param amount - Quantità di crediti rimborsati
 */
export const notifyCreditsRefunded = (amount: number) => {
  emitCreditsEvent(CREDITS_EVENTS.CREDITS_REFUNDED, { amount });
};

/**
 * Aggiunge un listener per gli eventi di aggiornamento crediti
 * @param callback - Funzione da eseguire quando l'evento viene emesso
 * @param eventType - Tipo di evento da ascoltare (default: CREDITS_UPDATED)
 */
export const addCreditsEventListener = (
  callback: (event: CustomEvent) => void,
  eventType: string = CREDITS_EVENTS.CREDITS_UPDATED
) => {
  const handler = (event: Event) => callback(event as CustomEvent);
  window.addEventListener(eventType, handler);
  
  // Restituisce una funzione per rimuovere il listener
  return () => window.removeEventListener(eventType, handler);
};

/**
 * Rimuove un listener per gli eventi di aggiornamento crediti
 * @param callback - Funzione callback da rimuovere
 * @param eventType - Tipo di evento (default: CREDITS_UPDATED)
 */
export const removeCreditsEventListener = (
  callback: (event: CustomEvent) => void,
  eventType: string = CREDITS_EVENTS.CREDITS_UPDATED
) => {
  window.removeEventListener(eventType, callback as EventListener);
};
